/**
 * Datos del reporte de ventas de un evento, ya calculados y listos para
 * volcarse a PDF, Excel o CSV. Los generadores no consultan nada: reciben
 * este objeto y solo lo presentan, así los tres formatos cuentan lo mismo.
 */
export interface ReportSummaryRow {
    label: string;
    /** Valor ya formateado (moneda, unidades, texto). */
    value: string;
    /** Valor crudo para Excel/CSV, si aplica (número sin formato). */
    raw?: number;
    /** 'money' | 'count' | 'text' para que Excel aplique el formato correcto. */
    kind: 'money' | 'count' | 'text';
}

/**
 * Un cajero dentro de una estación. La estación suma a todos sus cajeros:
 * los que vendieron y los asignados que no vendieron (en cero), para que
 * el subtotal de la barra sea la suma completa de sus usuarios.
 */
export interface ReportCashier {
    name: string;
    email: string;
    orders: number;
    /** Regalías (órdenes con total 0) del cajero; van dentro de `orders`. */
    courtesy?: number;
    cash: number;
    card: number;
    pos: number;
    total: number;
    /** false = vendió en esta barra pero está asignado a otra (o a ninguna). */
    assigned: boolean;
    /** Barra a la que está asignado según la configuración, si difiere. */
    assigned_to: string;
    /** Asignado a la barra pero sin ventas en el periodo. */
    no_sales: boolean;
}

export interface ReportStation {
    name: string;
    orders: number;
    /** Regalías (órdenes con total 0) de la estación; van dentro de `orders`. */
    courtesy?: number;
    cash: number;
    card: number;
    pos: number;
    cost_admin: number;
    total: number;
    /** 0-100 */
    pct: number;
    /** Desglose por cajero; vacío si el evento no resuelve cuentas. */
    cashiers?: ReportCashier[];
}

export interface ReportPayment {
    /** cash | card | pos | other */
    key: string;
    label: string;
    count: number;
    total: number;
    /** 0-100 */
    pct: number;
}

export interface ReportProduct {
    name: string;
    quantity: number;
    total: number;
    /** Entrada (boletería) en vez de producto. */
    ticket: boolean;
}

/** Lo que vendió una caja (cajero) dentro de una barra, producto por producto. */
export interface ReportCashierProducts {
    name: string;
    email: string;
    orders: number;
    units: number;
    total: number;
    products: ReportProduct[];
}

/**
 * Lo que vendió una barra, producto por producto, y el mismo detalle por
 * cada caja. Solo aparecen barras y cajas con ventas en el periodo.
 */
export interface ReportStationProducts {
    name: string;
    orders: number;
    units: number;
    total: number;
    products: ReportProduct[];
    cashiers: ReportCashierProducts[];
}

/** Lo regalado a una empresa en el periodo del informe. */
export interface ReportCourtesyCompany {
    company: string;
    orders: number;
    units: number;
    /** "3× Entrada General · 2× Cerveza" */
    detail: string;
}

/** Vendido contra regalado de un producto que se regaló alguna vez. */
export interface ReportCourtesyProduct {
    name: string;
    sold: number;
    courtesy: number;
    total: number;
}

/** Una regalía, orden por orden. */
export interface ReportCourtesyOrder {
    number: string;
    /** dd/MM/yyyy HH:mm o vacío. */
    date: string;
    company: string;
    units: number;
    detail: string;
    zone: string;
    cashier: string;
}

/**
 * Regalías del periodo: órdenes hechas con valor 0 (entradas o productos
 * regalados, casi siempre a una empresa patrocinadora). No suman a la venta;
 * el informe las presenta aparte con vendido / regalado / total.
 */
export interface ReportCourtesy {
    orders: number;
    units: number;
    /** Órdenes y unidades con cobro, para el contraste. */
    sold_orders: number;
    sold_units: number;
    companies: ReportCourtesyCompany[];
    products: ReportCourtesyProduct[];
    list: ReportCourtesyOrder[];
}

export interface EventReport {
    event_name: string;
    /** "11/09/2026" o "11/09/2026 – 12/09/2026". Vacío si el evento no tiene fecha. */
    event_dates: string;
    event_location: string;
    /** Código ISO (USD, HNL…) y símbolo corto ($, L…). */
    currency_code: string;
    currency_symbol: string;
    generated_at: Date;
    /** Quién generó el reporte (correo de la sesión). */
    generated_by: string;
    /** Evento de boletería: el resumen trae entradas, cargo, etc. */
    ticketing: boolean;

    summary: ReportSummaryRow[];
    stations: ReportStation[];
    /** El cargo de boletería solo se lista si el evento lo cobra. */
    show_cost_admin: boolean;
    /** Métodos que el responsable del evento dejó visibles: cash, card, pos. */
    visible_methods: string[];
    payments: ReportPayment[];
    products: ReportProduct[];
    /** Productos vendidos por barra y por caja (vacío si el evento no tiene barras). */
    station_products: ReportStationProducts[];
    /** Regalías del periodo; null si no hubo ninguna. */
    courtesy?: ReportCourtesy | null;

    totals: {
        orders: number;
        sales: number;
        items: number;
        cash: number;
        card: number;
        pos: number;
        cost_admin: number;
        /** Regalías incluidas en `orders`; sus unidades no están en `items`. */
        courtesy_orders?: number;
        courtesy_units?: number;
    };
}

/** Marca de la casa para cabeceras y pies. */
export const REPORT_BRAND = {
    company: 'Barlleno',
    tech: 'TecWave',
    wine: '#912721',
    wineDeep: '#5b1a1a',
    ink: '#1a1a2e',
    muted: '#6b7280',
    line: '#e5e7eb',
    zebra: '#f7f5f5',
    cashGreen: '#15803d',
};

/** Columnas de método de pago por estación, en el orden del cierre de caja. */
export const STATION_METHOD_COLUMNS: { key: string; label: string; field: 'cash' | 'card' | 'pos' }[] = [
    { key: 'cash', label: 'Efectivo', field: 'cash' },
    { key: 'card', label: 'Tarjeta', field: 'card' },
    { key: 'pos', label: 'POS', field: 'pos' },
];

export function stationMethodColumns(report: EventReport) {
    const visible = Array.isArray(report.visible_methods) && report.visible_methods.length > 0
        ? report.visible_methods
        : STATION_METHOD_COLUMNS.map(c => c.key);
    return STATION_METHOD_COLUMNS.filter(c => visible.indexOf(c.key) >= 0);
}

// ─── Formato compartido ────────────────────────────────────────────────────

/** 1677 → "$1,677.00" con el símbolo que se pase. */
export function formatMoney(value: number, symbol: string): string {
    const n = Number(value || 0);
    const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (n < 0 ? '-' : '') + symbol + abs;
}

export function formatInt(value: number): string {
    return Number(value || 0).toLocaleString('en-US');
}

export function pad2(n: number): string {
    return n < 10 ? '0' + n : String(n);
}

/** dd/MM/yyyy */
export function formatDate(d: Date): string {
    if (!d || isNaN(d.getTime())) return '';
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** dd/MM/yyyy HH:mm */
export function formatDateTime(d: Date): string {
    if (!d || isNaN(d.getTime())) return '';
    return `${formatDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Nombre de archivo sin caracteres raros: "Reporte_TINI_World_Tour_2026-09-12". */
export function reportFileName(report: EventReport, ext: string): string {
    const slug = String(report.event_name || 'Evento')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^A-Za-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 60) || 'Evento';
    const d = report.generated_at;
    const stamp = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    return `Reporte_${slug}_${stamp}.${ext}`;
}
